/**
 * useTodoDueDateMutation — Mutation hook for updating a todo's due date.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase, withSessionSetup } from "@/integrations/supabase/client";
import { useErrorHandler } from "@/hooks/useErrorHandler";
import { useAuth } from "@/hooks/useAuth";
import { formatDateForDatabase } from "@/hooks/goal-operations/helpers/dateUtils";
import { useCallback } from "react";
import { logger } from '@/utils/logger';

export const useTodoDueDateMutation = () => {
  const { handleError, handleSuccess } = useErrorHandler();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const updateTodoDueDateMutation = useMutation({
    mutationFn: async ({ todoId, newDate }: { todoId: string, newDate: Date | string | null }) => {
      if (!user) {
        throw new Error("User must be authenticated");
      }
      
      const formattedDate = formatDateForDatabase(newDate);
      
      // Use withSessionSetup to ensure proper session context
      const result = await withSessionSetup(async () => {
        // Use the RPC function for better performance
        const { data, error } = await supabase
          .rpc('update_todo_due_date', { 
            todo_id: todoId, 
            new_date: formattedDate 
          });
        
        if (error) {
          // If RPC fails, try direct update as fallback
          logger.warn('RPC failed, attempting direct update:', error);
          
          const { error: directError } = await supabase
            .from('todos')
            .update({ 
              due_date: formattedDate,
              updated_at: new Date().toISOString()
            })
            .eq('id', todoId);
          
          if (directError) {
            throw directError;
          }
        }
        
        return { todoId, formattedDate };
      });
      
      if (!result) {
        throw new Error("Failed to update due date - session setup error");
      }
      
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["goals"] });
      handleSuccess("Todo due date has been updated successfully.", { title: "Due date updated" });
    },
    onError: (error: Error) => {
      handleError(error, { title: "Error updating due date" });
    }
  });

  // Wrap exported function with useCallback to prevent unnecessary re-renders
  const updateTodoDueDate = useCallback((params: { goalTitle?: string, todoId: string, newDate: Date | string | null }) => {
    return new Promise((resolve, reject) => {
      updateTodoDueDateMutation.mutate({ 
        todoId: params.todoId, 
        newDate: params.newDate 
      }, {
        onSuccess: (data) => {
          resolve(data);
        },
        onError: (error) => {
          reject(error);
        }
      });
    });
  }, [updateTodoDueDateMutation]);

  return { updateTodoDueDate };
};
